import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import StatusBadge from "@/components/StatusBadge";
import InfoTip from "@/components/InfoTip";
import { Briefcase, ChevronDown, ChevronRight } from "lucide-react";

export default function WorkOrderDashboard() {
  const { api } = useAuth();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    api.get("/work-orders").then(r => setOrders(r.data)).catch(console.error).finally(() => setLoading(false));
  }, [api]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="work-orders-dashboard">
      <div className="flex items-center justify-between mb-8 animate-fadeIn">
        <div>
          <h1 className="text-2xl font-heading text-slate-900">Work Orders</h1>
          <p className="text-sm text-slate-500 mt-1">Track your outreach requests</p>
        </div>
      </div>

      {loading ? (
        <div className="space-y-3">{[1,2,3].map(i => <div key={i} className="h-16 bg-white rounded-xl border animate-pulse" />)}</div>
      ) : orders.length === 0 ? (
        <Card className="border-dashed border-slate-300 bg-slate-50/50" data-testid="wo-empty">
          <CardContent className="p-12 text-center">
            <Briefcase className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-heading text-slate-900 mb-2">No work orders yet</h3>
            <p className="text-sm text-slate-500">Create a work order from the Talent Explorer to initiate candidate outreach.</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {orders.map((wo, idx) => (
            <Card key={wo.id} className="rounded-xl border bg-card animate-slideUp" style={{ animationDelay: `${idx * 0.05}s` }} data-testid={`wo-row-${wo.id}`}>
              <CardContent className="p-0">
                <button onClick={() => setExpandedId(expandedId === wo.id ? null : wo.id)} className="w-full flex items-center gap-4 p-4 text-left">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-mono text-slate-400 uppercase">{wo.id?.slice(0, 8)}</span>
                      <StatusBadge status={wo.status} />
                      <StatusBadge status={wo.type === "job_promotion" ? "active_recruit" : "succession_planning"} />
                      {wo.priority === "urgent" && <Badge className="bg-red-100 text-red-700 border-0 text-xs">Urgent</Badge>}
                    </div>
                    <h3 className="text-sm font-heading text-slate-900">{wo.title}</h3>
                  </div>
                  <div className="text-right">
                    <p className="text-xs text-slate-400">{new Date(wo.created_at).toLocaleDateString()}</p>
                    <p className="text-xs text-slate-500 font-mono">{wo.candidate_pool?.length || 0} candidates</p>
                  </div>
                  {expandedId === wo.id ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronRight className="w-4 h-4 text-slate-400" />}
                </button>
                {expandedId === wo.id && (
                  <div className="px-4 pb-4 border-t border-slate-100 pt-4 animate-slideDown">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div><span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1">Outreach Sent <InfoTip text="Number of candidates the Varyynt team has contacted on your behalf through de-identified messaging." /></span><span className="text-lg font-accent font-bold text-slate-900">{wo.outreach_sent_count || 0}</span></div>
                      <div><span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1">Responses <InfoTip text="Candidates who replied to the outreach. A response means they engaged with the opportunity description." /></span><span className="text-lg font-accent font-bold text-slate-900">{wo.responses_received_count || 0}</span></div>
                      <div><span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1">Interested <InfoTip text="Candidates who expressed interest in learning more. This is where intent data proves its value -- these candidates are pre-qualified and directionally aligned." /></span><span className="text-lg font-accent font-bold text-lime-700">{wo.interested_count || 0}</span></div>
                      <div><span className="text-xs font-mono uppercase tracking-widest text-slate-400 flex items-center gap-1">Consented <InfoTip text="Candidates who agreed to be introduced to your hiring team. These are warm introductions, ready for your interview process." /></span><span className="text-lg font-accent font-bold text-teal-700">{wo.consented_count || 0}</span></div>
                    </div>
                    {wo.admin_notes && <p className="text-sm text-slate-600 mt-3 p-3 bg-slate-50 rounded-lg"><span className="text-xs font-mono text-slate-400 block mb-1">Admin Notes</span>{wo.admin_notes}</p>}
                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {wo.candidate_pool?.map(id => <Badge key={id} variant="outline" className="font-mono text-xs">{id}</Badge>)}
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
